import axios from 'axios';
import { getLang } from '@app/i18n';
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  getTempToken,
  getTokenType,
  setAuthTokens,
} from '@app/api/tokenService';

// Окружение определяется по домену, на котором открыт фронт.
// Прод — только app.moneta.ad. Всё остальное (стейдж, localhost,
// превью-сборки) по умолчанию работает со стейджевым бэкендом:
// ошибка в сторону стейджа безопасна, в сторону прода — нет.
export const isProd = window.location.hostname === 'app.moneta.ad';
const isLocalhost = window.location.hostname === 'localhost';

const baseApiUrl = isProd
  ? 'https://api.adveo.agency/api/frontend'
  : 'https://stage.adveo.dev/api/frontend';

export const baseFrontendUrl = isLocalhost
  ? 'http://localhost:3000'
  : isProd
    ? 'https://app.moneta.ad'
    : 'https://app.adveo.dev';

export const instance = axios.create({
  baseURL: baseApiUrl,
});

const twoFaLoginEndpoint = '/login/google2fa';

const authBypassEndpoints = [
  '/login',
  twoFaLoginEndpoint,
  '/registration',
  '/password/forgot',
  '/password/reset',
  '/setpassword',
  '/order',
];

const forceLogout = () => {
  clearSession();
  window.location.href = '/login';
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  failedQueue = [];
};

instance.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = getLang();

  const tokenType = getTokenType();
  const authToken = config.url?.includes(twoFaLoginEndpoint)
    ? getTempToken()
    : getAccessToken();

  if (authToken) {
    config.headers.Authorization = `${tokenType} ${authToken}`;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    const status = response?.status;

    if (
      status === 403 &&
      !response.data?.message &&
      response.data?.data?.message
    ) {
      response.data.message = response.data.data.message;
    }

    if (!config || config.__isRetryRequest) {
      if (status === 401) {
        forceLogout();
      }
      throw error;
    }

    if (status === 401) {
      const refreshToken = getRefreshToken();
      const shouldBypassRedirect = authBypassEndpoints.some((path) =>
        config.url?.includes(path),
      );

      if (shouldBypassRedirect) {
        throw error;
      }

      if (!refreshToken) {
        forceLogout();
        throw error;
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          config.__isRetryRequest = true;
          return instance(config);
        });
      }

      config.__isRetryRequest = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${baseApiUrl}/refresh-token`, {
          refresh_token: refreshToken,
          secure_mode: false,
        });
        setAuthTokens(data);
        processQueue(null);
        return instance(config);
      } catch (refreshError) {
        processQueue(refreshError);
        forceLogout();
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }

    throw error;
  },
);
